/**
 * 端末内アカウントの画面部品（登録・ログイン・切り替え）。
 *
 * ケア画面（未ログインのとき）と設定画面の両方から同じものを使う。
 * アカウントはこの端末の中だけにあり、サーバには作らない（accounts.ts の注意書き参照）。
 */
import {
  ACCOUNT_LOCAL_NOTE,
  ACCOUNT_SCOPE_NOTE,
  ACCOUNT_SECURITY_NOTE,
  MIN_PASSWORD_LENGTH,
  changePassword,
  createAccount,
  findAccountByName,
  validatePassword,
  validateUserName,
  verifyPassword,
} from "@uvk/shared";
import {
  adoptRecordsIfFirstUser,
  currentUser,
  reloadUsers,
  setCurrentUser,
  state,
} from "../state.js";
import { deleteUser, saveUser } from "../db.js";
import { esc } from "../ui.js";

/** 画面の下に出す一言（成功・失敗どちらも同じ場所に出す） */
let message = "";
let messageKind: "ok" | "ng" = "ok";
/** 未ログイン時に、ログインと新規登録のどちらを開いているか */
let mode: "login" | "register" = "login";

function say(text: string, kind: "ok" | "ng" = "ok"): void {
  message = text;
  messageKind = kind;
}

function messageHtml(): string {
  if (!message) return "";
  const color = messageKind === "ok" ? "var(--ink)" : "var(--warn)";
  return `<p class="sub" style="color:${color};margin-top:8px">${esc(message)}</p>`;
}

function formHtml(): string {
  const first = state.users.length === 0;
  // はじめての人には選択肢を見せない（作るしかないため）
  const tabs = first
    ? ""
    : `<div class="row" style="margin-bottom:10px">
         <button class="ghost grow ${mode === "login" ? "selected" : ""}" data-acc-mode="login">ログイン</button>
         <button class="ghost grow ${mode === "register" ? "selected" : ""}" data-acc-mode="register">新しく作る</button>
       </div>`;
  const registering = first || mode === "register";
  return `
    ${tabs}
    <label class="field">ユーザー名
      <input type="text" id="acc-name" autocomplete="username" placeholder="例：みー" maxlength="20" />
    </label>
    <label class="field">パスワード
      <input type="password" id="acc-pw" placeholder="${MIN_PASSWORD_LENGTH}文字以上"
        autocomplete="${registering ? "new-password" : "current-password"}" />
    </label>
    ${
      registering
        ? `<label class="field">パスワード（確認）
             <input type="password" id="acc-pw2" autocomplete="new-password" />
           </label>`
        : ""
    }
    <button id="acc-submit" class="primary" style="width:100%;margin-top:10px">
      ${registering ? "このユーザーを作る" : "ログイン"}
    </button>
    ${messageHtml()}
    <p class="sub" style="margin-top:10px">${esc(ACCOUNT_LOCAL_NOTE)}</p>
    <p class="sub">${esc(ACCOUNT_SECURITY_NOTE)}</p>`;
}

function signedInHtml(): string {
  const me = currentUser()!;
  const others = state.users.filter((u) => u.id !== me.id);
  return `
    <p><b>${esc(me.name)}</b> でログイン中</p>
    <p class="sub">${esc(ACCOUNT_SCOPE_NOTE)}</p>
    <div class="row" style="margin-top:10px">
      <button id="acc-logout" class="ghost grow">ログアウト</button>
      <button id="acc-add" class="ghost grow">ユーザーを追加</button>
    </div>
    ${
      others.length
        ? `<p class="sub" style="margin-top:12px">切り替え</p>
           <div class="row">
             ${others
               .map((u) => `<button class="ghost" data-acc-switch="${esc(u.id)}">${esc(u.name)}</button>`)
               .join("")}
           </div>
           <p class="sub">切り替えにはパスワードが必要です。</p>`
        : ""
    }
    <details style="margin-top:12px">
      <summary class="sub">パスワードを変える／このユーザーを消す</summary>
      <label class="field">いまのパスワード
        <input type="password" id="acc-old" autocomplete="current-password" />
      </label>
      <label class="field">新しいパスワード
        <input type="password" id="acc-new" autocomplete="new-password" />
      </label>
      <div class="row" style="margin-top:8px">
        <button id="acc-chpw" class="ghost grow">パスワードを変える</button>
        <button id="acc-del" class="ghost" style="color:var(--warn)">このユーザーを消す</button>
      </div>
      <p class="sub">消すと、そのユーザーのケア記録と肌年齢の測定値も一緒に消えます。</p>
    </details>
    ${messageHtml()}`;
}

/** 設定画面に置くカードの中身（見出しはこちらで持つ） */
export function accountCardHtml(): string {
  return `
    <div class="card" id="account-card">
      <h2>👤 ユーザー</h2>
      ${currentUser() ? signedInHtml() : formHtml()}
    </div>`;
}

/**
 * ケア画面が未ログインのときに出す案内。
 * 「なぜ登録が要るのか」を先に書く（記録が人ごとに分かれることが理由）。
 */
export function accountGateHtml(): string {
  return `
    <div class="card">
      <h2>👤 ユーザーを選ぶ</h2>
      <p class="sub">${esc(ACCOUNT_SCOPE_NOTE)}1台のスマホを家族で使い分けられます。</p>
      ${formHtml()}
    </div>`;
}

/**
 * ボタンをつなぐ。`rerender` は呼び出し側の再描画（設定・ケアで別物）。
 * 画面を作り直すたびに呼ぶこと。
 */
export function bindAccount(el: HTMLElement, rerender: () => void): void {
  const val = (id: string): string => el.querySelector<HTMLInputElement>(id)?.value ?? "";

  el.querySelectorAll<HTMLButtonElement>("[data-acc-mode]").forEach((b) => {
    b.onclick = () => {
      mode = b.dataset.accMode as "login" | "register";
      say("");
      rerender();
    };
  });

  const submit = el.querySelector<HTMLButtonElement>("#acc-submit");
  if (submit) {
    submit.onclick = async () => {
      const name = val("#acc-name");
      const pw = val("#acc-pw");
      const registering = state.users.length === 0 || mode === "register";
      submit.disabled = true;
      try {
        if (registering) {
          const nameCheck = validateUserName(name, state.users);
          if (!nameCheck.ok) return say(nameCheck.reason, "ng"), rerender();
          const pwCheck = validatePassword(pw);
          if (!pwCheck.ok) return say(pwCheck.reason, "ng"), rerender();
          if (pw !== val("#acc-pw2")) return say("パスワードが一致しません", "ng"), rerender();

          const account = await createAccount(name, pw, Date.now(), state.users);
          await saveUser(account);
          await reloadUsers();
          // アカウントを入れる前の記録が消えたように見えないよう、最初の人が引き取る
          const moved = await adoptRecordsIfFirstUser(account.id);
          await setCurrentUser(account.id);
          say(moved > 0 ? `${account.name} を作りました（前からの記録${moved}件を引き継ぎました）` : `${account.name} を作りました`);
        } else {
          const account = findAccountByName(state.users, name);
          // 「そのユーザーはいません」と分けて答えると、名前の当てっこができてしまう
          if (!account || !(await verifyPassword(account, pw))) {
            return say("ユーザー名かパスワードが違います", "ng"), rerender();
          }
          await setCurrentUser(account.id);
          say(`${account.name} でログインしました`);
        }
      } catch (e) {
        say(e instanceof Error ? e.message : "うまくいきませんでした", "ng");
      } finally {
        submit.disabled = false;
        rerender();
      }
    };
  }

  const logout = el.querySelector<HTMLButtonElement>("#acc-logout");
  if (logout) {
    logout.onclick = async () => {
      await setCurrentUser(null);
      mode = "login";
      say("ログアウトしました");
      rerender();
    };
  }

  const add = el.querySelector<HTMLButtonElement>("#acc-add");
  if (add) {
    add.onclick = async () => {
      await setCurrentUser(null);
      mode = "register";
      say("");
      rerender();
    };
  }

  el.querySelectorAll<HTMLButtonElement>("[data-acc-switch]").forEach((b) => {
    b.onclick = async () => {
      const target = state.users.find((u) => u.id === b.dataset.accSwitch);
      if (!target) return;
      // 切り替えも本人確認する。しないと仕切りの意味がなくなる
      const pw = prompt(`${target.name} のパスワード`);
      if (pw === null) return;
      if (!(await verifyPassword(target, pw))) {
        say("パスワードが違います", "ng");
        return rerender();
      }
      await setCurrentUser(target.id);
      say(`${target.name} に切り替えました`);
      rerender();
    };
  });

  const chpw = el.querySelector<HTMLButtonElement>("#acc-chpw");
  if (chpw) {
    chpw.onclick = async () => {
      const me = currentUser();
      if (!me) return;
      if (!(await verifyPassword(me, val("#acc-old")))) {
        say("いまのパスワードが違います", "ng");
        return rerender();
      }
      const check = validatePassword(val("#acc-new"));
      if (!check.ok) {
        say(check.reason, "ng");
        return rerender();
      }
      await saveUser(await changePassword(me, val("#acc-new")));
      await reloadUsers();
      say("パスワードを変えました");
      rerender();
    };
  }

  const del = el.querySelector<HTMLButtonElement>("#acc-del");
  if (del) {
    del.onclick = async () => {
      const me = currentUser();
      if (!me) return;
      if (!(await verifyPassword(me, val("#acc-old")))) {
        say("消すには、いまのパスワードを入れてください", "ng");
        return rerender();
      }
      if (!confirm(`${me.name} と、そのケア記録・肌年齢の測定値を消します。よろしいですか？`)) return;
      await deleteUser(me.id);
      await setCurrentUser(null);
      await reloadUsers();
      mode = "login";
      say("消しました");
      rerender();
    };
  }
}
